import { useEffect, useState } from 'react';
import { Api } from '../api';
import { useQuery } from '@tanstack/react-query';
import query from '../../constants/queries';
import { GetCase } from '../Cases';
import { useParams } from 'react-router-dom';

interface IOutboundSMS {
    id: string;
    to_number: string;
    from_number: string;
    outbound: boolean;
    content: string;
}

interface IOutboundCall {
    id: string;
    to_number: string;
    from_number: string;
    outbound: boolean;
    start_time: string;
    end_time: string;
}

export async function useRingCentralWidget() {
    const [ringCentralLoaded, setRingCentralLoaded] = useState(false);
    const { caseId } = useParams();
    const { data: caseDetail } = useQuery([query.caseDetail, caseId], () => GetCase(caseId || ''));
    let intervalId: NodeJS.Timeout | null = null;

    const isWidgetReady = () => {
        const widget = document.getElementById('rc-widget');

        if (widget) {
            setRingCentralLoaded(true);
            if (intervalId) {
                clearInterval(intervalId);
            }
            intervalId = null;
        }
    };

    const showRingCentralWidget = () => {
        if (!ringCentralLoaded) {
            const rcs = document.createElement('script');
            rcs.src = 'https://ringcentral.github.io/ringcentral-embeddable/adapter.js';
            document.body.appendChild(rcs);

            if (!intervalId) {
                intervalId = setInterval(isWidgetReady, 1000);
            }
        }
    };

    const postOutboundSMS = async (caseId: string | number, smsData: IOutboundSMS) => {
        try {
            const request = await Api.post(`case/${caseId}/outbound/sms`, smsData);

            return request.data;
        } catch (error: any) {
            return error;
        }
    };

    const postOutboundCall = async (caseId: string | number, callData: IOutboundCall) => {
        try {
            const request = await Api.post(`case/${caseId}/outbound/call`, callData);

            return request.data;
        } catch (error: any) {
            return error;
        }
    };

    useEffect(() => {
        showRingCentralWidget();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        window.addEventListener('message', (e) => {
            const data = e.data;

            if (!caseDetail || !caseDetail.id) return;

            if (data) {
                switch (data.type) {
                    case 'rc-call-end-notify':
                        // get call on call end event
                        postOutboundCall(caseDetail.id, {
                            id: data.call.callId,
                            to_number: data.call.to,
                            from_number: data.call.from,
                            outbound: data.call.direction === 'Outbound',
                            start_time: data.call.startTime,
                            end_time: data.call.endTime
                        });
                        break;
                    case 'rc-message-updated-notify':
                        // get call on call muted or unmuted event
                        postOutboundSMS(caseDetail.id, {
                            id: data.message.id,
                            to_number: data.message.to[0].phoneNumber,
                            from_number: data.message.from.phoneNumber,
                            outbound: data.message.direction === 'Outbound',
                            content: data.message.subject
                        });
                        break;
                    default:
                        break;
                }
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ringCentralLoaded]);
}

export const postOutboundCallAttempt = async (caseId: string | number, calleeNumber: string) => {
    try {
        const request = await Api.post(`case/${caseId}/outbound/call/attempt`, { to: calleeNumber });

        return request.data;
    } catch (error: any) {
        if (error.response && error.response.data.error) {
            throw new Error(error.response.data.error);
        }
        throw error;
    }
};
